import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { InputParam } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";
import type { KioskCacheItem, LocationCacheItem } from "../../../types/session.ts";
import {
  ChevronIcon,
  FloatingPopover,
  OPERATOR_DEFAULT_LABEL,
  sharedStyles as s,
  XIcon,
} from "./identityVariants/shared.tsx";

// Matches WebAdmin2 grid/groupby.ts — drives the 2-level "group by column, then value"
// filter: emits sGroupBy + (optional) sGroupValue InputParams.

interface GroupOpt {
  id: string;             // emitted as sGroupBy value
  label: string;          // UI label
  source: "kiosk" | "location" | "operator";
  field?: keyof KioskCacheItem | keyof LocationCacheItem;
}

const BASE_GROUPS: GroupOpt[] = [
  { id: "sType", label: "Type", source: "kiosk", field: "Type" },
  { id: "sAddress", label: "Address", source: "location", field: "Address" },
  { id: "sZipCode", label: "Zip", source: "location", field: "ZipCode" },
  { id: "sDistrict", label: "State", source: "location", field: "District" },
  { id: "sCity", label: "City", source: "location", field: "City" },
  { id: "sStoreName", label: "Store Name", source: "location", field: "StoreName" },
  { id: "sCountry", label: "Country", source: "location", field: "Country" },
];

/** id → label map for all group-by options, including operator. Used by the
 *  chip/pill renderer to translate raw sGroupBy values. */
export const GROUP_BY_LABELS: Record<string, string> = {
  ...Object.fromEntries(BASE_GROUPS.map((g) => [g.id, g.label])),
  iOwnerOperatorID: "Owner/Operator ID",
};

const NONE_VALUE = "__none__";

function paramsFor(group: string, rawValue: string, isParameter: boolean): InputParam[] {
  if (!group || group === "ALL") return [];
  const out: InputParam[] = [{ sColumn: "sGroupBy", oValue: group, Type: ParamType.Equal }];
  const val = rawValue === NONE_VALUE ? "" : rawValue;
  if (isParameter || val !== "") {
    out.push({ sColumn: "sGroupValue", oValue: val, Type: ParamType.Equal });
  }
  return out;
}

function extractState(value: InputParam[]): { group: string; value: string } {
  let group = "ALL";
  let val = "";
  for (const p of value) {
    if (p.sColumn === "sGroupBy" && p.oValue != null) group = String(p.oValue);
    if (p.sColumn === "sGroupValue" && p.oValue != null) val = String(p.oValue);
  }
  return { group, value: val };
}

export function GroupByFilter({ isParameter, value, onChange, cache }: AdvancedFilterProps) {
  const initial = useMemo(() => extractState(value), [value]);
  const [group, setGroup] = useState(initial.group);
  const [val, setVal] = useState(initial.value || (initial.group !== "ALL" ? NONE_VALUE : ""));

  // Rehydrate when parent pushes new value (e.g. saved filter load / clear)
  useEffect(() => {
    const s = extractState(value);
    setGroup(s.group);
    setVal(s.value || (s.group !== "ALL" ? NONE_VALUE : ""));
  }, [value]);

  const groupOptions = useMemo<GroupOpt[]>(() => {
    const out: GroupOpt[] = [{ id: "ALL", label: "ALL", source: "kiosk" }, ...BASE_GROUPS];
    if (!cache.OwnerOperatorID) {
      out.push({ id: "iOwnerOperatorID", label: "Owner/Operator ID", source: "operator" });
    }
    return out;
  }, [cache.OwnerOperatorID]);

  const valueOptions = useMemo<{ id: string; label: string; count: number }[]>(() => {
    if (group === "ALL") return [];
    const opt = groupOptions.find((o) => o.id === group);
    if (!opt) return [];
    const counts = new Map<string, { label: string; count: number }>();
    if (opt.source === "operator") {
      // Count by OwnerOperatorID on locations (the primary "owns" association).
      const locs = (cache.CommonData?.Location ?? []) as LocationCacheItem[];
      for (const l of locs) {
        const raw = l.OwnerOperatorID;
        const idNum = raw == null ? 0 : raw;
        const id = String(idNum);
        const cur = counts.get(id);
        if (cur) cur.count++;
        else counts.set(id, { label: id, count: 1 });
      }
      // Ensure Default always present even if no items fall into it.
      if (!counts.has("0")) counts.set("0", { label: "0", count: 0 });
      // Resolve id → display name via Common.
      const common = (cache.CommonData?.Common ?? []) as Array<{ ColumnName?: string; TypeName?: string; sName?: string; Value?: unknown; sFilterValue?: unknown; Title?: unknown; sDisplayText?: unknown }>;
      for (const c of common) {
        const type = (c.ColumnName ?? c.TypeName ?? c.sName ?? "").toLowerCase();
        if (type !== "iowneroperatorid") continue;
        const id = c.Value != null ? String(c.Value) : c.sFilterValue != null ? String(c.sFilterValue) : "";
        const name = c.Title != null ? String(c.Title) : c.sDisplayText != null ? String(c.sDisplayText) : id;
        const cur = counts.get(id);
        if (cur) cur.label = name;
        else counts.set(id, { label: name, count: 0 });
      }
      // Rewrite "0" / "" bucket label to "Default".
      const zero = counts.get("0");
      if (zero) zero.label = OPERATOR_DEFAULT_LABEL;
    } else {
      const src = opt.source === "kiosk"
        ? (cache.CommonData?.Kiosk ?? [])
        : (cache.CommonData?.Location ?? []);
      const field = opt.field as string;
      for (const item of src) {
        const raw = (item as unknown as Record<string, unknown>)[field];
        if (raw == null || raw === "") continue;
        const str = String(raw);
        const cur = counts.get(str);
        if (cur) cur.count++;
        else counts.set(str, { label: str, count: 1 });
      }
    }
    return [...counts.entries()]
      .map(([id, v]) => ({ id, label: v.label, count: v.count }))
      .sort((a, b) => {
        if (a.label === OPERATOR_DEFAULT_LABEL) return -1;
        if (b.label === OPERATOR_DEFAULT_LABEL) return 1;
        const aX = a.label.startsWith("❌");
        const bX = b.label.startsWith("❌");
        if (aX !== bX) return aX ? 1 : -1;
        return a.label.localeCompare(b.label);
      });
  }, [group, groupOptions, cache.CommonData]);

  const emit = useCallback(
    (nextGroup: string, nextVal: string) => {
      onChange(paramsFor(nextGroup, nextVal, isParameter));
    },
    [onChange, isParameter],
  );

  const [open, setOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  const anchorRef = useRef<HTMLButtonElement>(null);

  const pickGroup = (g: string) => {
    setGroup(g);
    setVal(NONE_VALUE);
    setBulkSearch("");
    emit(g, "");
  };

  const pickValue = (v: string) => {
    setVal(v);
    emit(group, v);
    setOpen(false);
    // Intentionally no onSearch() — emit() is async (setFilterState → effect →
    // parent), so firing the search here races and hits the server with the
    // previous sGroupValue. User clicks Search when ready.
  };

  const filteredValueOptions = useMemo(() => {
    if (!bulkSearch.trim()) return valueOptions;
    const lc = bulkSearch.toLowerCase();
    return valueOptions.filter((o) => o.label.toLowerCase().includes(lc));
  }, [valueOptions, bulkSearch]);

  const hasSelection = group !== "ALL";
  const activeGroupLabel = groupOptions.find((o) => o.id === group)?.label ?? group;
  const activeValueLabel = (() => {
    if (!hasSelection) return "";
    if (!val || val === NONE_VALUE) return "(none)";
    return valueOptions.find((o) => o.id === val)?.label ?? val;
  })();

  const clear = useCallback(() => {
    setGroup("ALL");
    setVal("");
    emit("ALL", "");
  }, [emit]);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-0-5)" }}>
      <button
        ref={anchorRef}
        type="button"
        className={hasSelection ? s.btnPillActive : s.btn}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
      >
        {hasSelection ? (
          <>
            <span>{activeGroupLabel}:</span>
            <span style={activeValueLabel === "(none)" || activeValueLabel === OPERATOR_DEFAULT_LABEL ? { fontStyle: "italic" } : undefined}>{activeValueLabel}</span>
          </>
        ) : (
          <span style={{ color: "var(--color-text-muted)" }}>Any group</span>
        )}
        <ChevronIcon />
      </button>
      {hasSelection && (
        <button type="button" className={s.btnGhost} onClick={clear} title="Clear" aria-label="Clear group">
          <XIcon size={11} />
        </button>
      )}

      <FloatingPopover
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        width={360}
        className={s.popover}
      >
        <div style={{ padding: "var(--space-2)" }}>
          {/* Group-field picker — same segmented-pill style as the Bulk tab. */}
          <div className={s.seg} style={{ flexWrap: "wrap", marginBottom: "var(--space-2)" }}>
            {groupOptions
              .filter((o) => o.id !== "ALL")
              .map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={group === o.id ? s.segBtnActive : s.segBtn}
                  onClick={() => pickGroup(o.id)}
                >
                  {o.label}
                </button>
              ))}
          </div>
          {group !== "ALL" && (
            <input
              className={s.input}
              style={{ width: "100%", height: 28, marginBottom: "var(--space-1)" }}
              value={bulkSearch}
              onChange={(e) => setBulkSearch(e.target.value)}
              placeholder={`Filter ${valueOptions.length} value${valueOptions.length === 1 ? "" : "s"}…`}
            />
          )}
          {group !== "ALL" && (
            <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)" }}>
              {filteredValueOptions.map((o) => {
                const isDefault = o.label === OPERATOR_DEFAULT_LABEL;
                const selected = val === o.id;
                return (
                  <label key={o.id} className={s.check} style={selected ? { background: "var(--color-bg-muted)" } : undefined}>
                    <input
                      type="radio"
                      name="groupByValue"
                      checked={selected}
                      onChange={() => pickValue(o.id)}
                    />
                    <span style={isDefault ? { fontStyle: "italic" } : undefined}>{o.label}</span>
                    <span className={s.countBadge}>{o.count}</span>
                  </label>
                );
              })}
              {!filteredValueOptions.length && (
                <div style={{ padding: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                  {valueOptions.length ? "No matches." : "No values."}
                </div>
              )}
            </div>
          )}
          {group === "ALL" && (
            <div style={{ padding: "var(--space-3) var(--space-2)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
              Pick a group field to see its values.
            </div>
          )}
        </div>
        <div className={s.popFooter}>
          {hasSelection && (
            <button type="button" className={s.btnGhost} onClick={clear}>Clear</button>
          )}
          <div className={s.spacer} />
          <button type="button" className={s.btn} onClick={() => setOpen(false)}>Done</button>
        </div>
      </FloatingPopover>
    </div>
  );
}
